import { useEffect, useState } from 'react'

import { ChevronLeft, ChevronRight, X } from 'lucide-react'

import { useGisIssueDetail } from '../../../hooks/useGisIssueDetail'
import { DetailOverlaySkeleton } from '../detail/DetailShell'
import { IssueDetailBody } from '../detail/IssueDetailBody'

import type { GisScope, GisSite } from '../../../api/types'
import type { HodOpenRef } from '../../../lib/detail-stack'

/** Host list's "n of N" pager shown at the accordion root (the Issues board passes one;
 *  site/HOD hosts omit it). */
export interface InlinePager {
  position: number
  total: number
  onPrev: (() => void) | null
  onNext: (() => void) | null
}

interface IssueInlineDetailProps {
  issueId: string
  sites: GisSite[]
  scope: GisScope
  /** Root back label — names the host screen ("Back to Issues" / "Back to site" / …). */
  backLabel?: string
  pager?: InlinePager | null
  onBack: () => void
  onOpenHod: (hod: HodOpenRef) => void
  onSelectOfficer: (id: string, name: string) => void
  onSelectMandal: (name: string) => void
}

/** Inline accordion detail: the shared body under a Back / "n of N" / Prev-Next / ✕ header. Holds a
 *  LOCAL drill stack so clicking a related issue (from the inline site/department lists) swaps the body
 *  to it — "← Back" returns to the exact previous issue; at the root the host pager + `backLabel`
 *  apply. Fetch-by-id means any related issue works regardless of the host's current filters/paging. */
export function IssueInlineDetail({ issueId, sites, scope, backLabel = 'Back to Issues', pager = null, onBack, onOpenHod, onSelectOfficer, onSelectMandal }: IssueInlineDetailProps) {
  const [stack, setStack] = useState<string[]>([issueId])
  // Reset the drill stack when the ROOT issue changes (board prev/next / a new row expands).
  useEffect(() => {
    setStack([issueId])
  }, [issueId])
  const currentId = stack[stack.length - 1] ?? issueId
  const drilled = stack.length > 1
  const { data: issue, isPending, isError } = useGisIssueDetail(currentId)

  const openRelated = (id: string) => setStack((s) => [...s, id])
  const back = () => (drilled ? setStack((s) => s.slice(0, -1)) : onBack())

  return (
    <div
      data-issue-detail={issueId}
      tabIndex={-1}
      role="region"
      aria-label="Issue detail"
      className="gis-accordion-in rounded-[12px] border border-gis-line bg-white outline-none"
    >
      <div className="flex items-center gap-3 border-b border-gis-line-2 px-4 py-[11px]">
        <button type="button" onClick={back} className="flex items-center gap-[5px] rounded-[9px] py-[6px] pr-2.5 pl-1.5 text-[12px] font-semibold text-gis-ink-2 transition hover:bg-[#f1f4f9] hover:text-gis-ink focus-visible:ring-2 focus-visible:ring-blue-500/60">
          <ChevronLeft className="h-[16px] w-[16px] stroke-[2.2]" /> {drilled ? 'Back' : backLabel}
        </button>
        <div className="ml-auto flex items-center gap-2">
          {pager && !drilled && (
            <>
              <span className="text-[11.5px] font-semibold tabular-nums text-gis-muted">{pager.position} of {pager.total}</span>
              <button type="button" onClick={() => pager.onPrev?.()} disabled={!pager.onPrev} aria-label="Previous issue" className="grid h-[28px] w-[28px] place-items-center rounded-md text-gis-soft transition hover:bg-gis-track disabled:opacity-40 focus-visible:ring-2 focus-visible:ring-blue-500/60">
                <ChevronLeft className="h-4 w-4" />
              </button>
              <button type="button" onClick={() => pager.onNext?.()} disabled={!pager.onNext} aria-label="Next issue" className="grid h-[28px] w-[28px] place-items-center rounded-md text-gis-soft transition hover:bg-gis-track disabled:opacity-40 focus-visible:ring-2 focus-visible:ring-blue-500/60">
                <ChevronRight className="h-4 w-4" />
              </button>
            </>
          )}
          <button type="button" onClick={onBack} aria-label="Close detail" className="grid h-[28px] w-[28px] place-items-center rounded-md text-gis-soft transition hover:bg-gis-track focus-visible:ring-2 focus-visible:ring-blue-500/60">
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
      <div className="px-4 pt-4 pb-5">
        {isPending ? (
          <DetailOverlaySkeleton />
        ) : isError || !issue ? (
          <div className="py-10 text-center text-[12px] text-gis-soft">Issue not found or no longer accessible.</div>
        ) : (
          <IssueDetailBody issue={issue} sites={sites} scope={scope} onOpenIssue={openRelated} onOpenHod={onOpenHod} onSelectOfficer={onSelectOfficer} onSelectMandal={onSelectMandal} />
        )}
      </div>
    </div>
  )
}
